import { useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MessageDTO, MessageAction } from '../../Types/MessageT';
import { MessageContext } from '../Context/MessageContextV2';
import { MessageS } from '../MessageS';
import { PostboxCountersContext } from '../Context/PostboxCountersContext';
import { ProfileContext } from '../Context/ProfileContext';
import { ToastS } from '../ToastS';

// eslint-disable-next-line import/prefer-default-export
export const useBookitupFolderMessages = (contactIds?: number[]) => {
  const { box, filter, id: eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { unreadMessagesProps } = useContext(ProfileContext);
  const { changeByAction, changeMessage, message, action } = useContext(MessageContext);
  const { refreshCount } = useContext(PostboxCountersContext);
  const [messages, setMessages] = useState<MessageDTO[]>([]);
  const [selectedMessage, selectMessage] = useState<MessageDTO>();
  const [loading, setLoading] = useState(false);
  const [searchQuery, updateQuery] = useState<string>();
  const [pageNumber, setPageNumber] = useState(0);
  const [changingSeenFlag, setChangingSeenFlag] = useState(false);
  const [trigger, setTrigger] = useState(Date.now());
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const query = searchParams.get('query');
  const emailId = searchParams.get('emailId');
  const entityId = searchParams.get('entityId');
  const { increment, decrement } = unreadMessagesProps;

  useEffect(() => {
    const abortController = new AbortController();
    fetchMessages(abortController);
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box, filter, pageNumber, query, trigger, searchQuery]);

  useEffect(() => {
    if (message && action !== 'EDIT') {
      changeMessage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMessage]);

  useEffect(() => {
    if (selectedMessage) {
      selectMessage(undefined);
    }
    if (pageNumber !== 0) {
      setPageNumber(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box]);

  useEffect(() => {
    if (emailId) {
      const optionalEmail = messages.find((e) => e.id === parseInt(emailId, 10));
      if (optionalEmail) {
        selectMessage(optionalEmail);
      }
    } else if (window.innerWidth > 700 && !entityId) {
      selectMessage(messages[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId, messages]);

  const fetchMessages = async (abortController: AbortController) => {
    try {
      setLoading(true);
      // eslint-disable-next-line no-underscore-dangle
      let _messages: MessageDTO[] = [];
      if (query) {
        _messages = await MessageS.searchMessages(query);
      } else {
        const page = await MessageS.fetchEmails(
          box ?? '',
          filter ?? '',
          pageNumber,
          contactIds,
          abortController.signal,
          searchQuery,
        );
        _messages = page.messages;
        setTotalCount(page.count);
        setPageCount(page.pages);
      }
      setMessages(_messages);
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const changeEmail = (message: MessageDTO) => setMessages(messages.map((m) => (m.id === message.id ? message : m)));

  const prepareMessage = async (selectedAction: MessageAction) => {
    if (selectedMessage) {
      // General postbox with assigned event --> Redirect to event scope
      if (!eventId && selectedMessage.eventId) {
        navigate(
          `/events/${selectedMessage.eventId}/emails/${box}?action=${selectedAction}&messageId=${selectedMessage.id}`,
        );
      } else {
        // We are already in event scope or message doesn't have an event assigned yet
        if (selectedMessage.imap) {
          changeByAction(null, selectedAction, selectedMessage);
          return;
        }
        changeByAction(selectedMessage.id, selectedAction);
      }
    }
  };

  const flushMessage = () => {
    if (selectedMessage) {
      changeEmail(selectedMessage);
    }
    changeMessage(null);
  };

  const refresh = () => setTrigger(Date.now());

  const changeSeenFlag = async (read: boolean) => {
    if (selectedMessage) {
      setChangingSeenFlag(true);
      const patchedEmail = await MessageS.patchEmail(selectedMessage.id, { read });
      if (patchedEmail) {
        ToastS.info(
          `seen-flag-changed-${selectedMessage.id}`,
          `Nachricht wurde als ${read ? 'gelesen' : 'ungelesen'} markiert.`,
        );
        refreshCount('unread');
        if (read) {
          decrement();
        } else {
          increment();
        }
        changeEmail(patchedEmail);
      }
      setChangingSeenFlag(false);
    }
  };

  const markMessagesInFolderAsRead = async () => {
    const unreadMessages = messages.filter(
      ({ id, localDateTime, readTimestamp, sendByUser }) => id && localDateTime && !readTimestamp && !sendByUser,
    );
    if (unreadMessages.length === 0) {
      return;
    }

    setChangingSeenFlag(true);
    const readTimestamp = new Date();
    const ids = unreadMessages.map(({ id }) => id);
    const ok = await MessageS.markChatMessagesAsRead(ids);
    if (ok) {
      ToastS.info('folder-messages-read', 'Nachrichten wurden als gelesen markiert.');
      refreshCount('unread');
      unreadMessagesProps.refresh();
      setMessages(messages.map((m) => (ids.includes(m.id) ? { ...m, readTimestamp } : m)));
      if (selectedMessage && ids.includes(selectedMessage.id)) {
        selectMessage({ ...selectedMessage, readTimestamp });
      }
    }
    setChangingSeenFlag(false);
  };

  const value = useMemo(
    () => ({
      messages,
      loading,
      selectedMessage,
      changingSeenFlag,
      searchQuery,
      updateQuery,
      changeSeenFlag,
      markMessagesInFolderAsRead,
      pageNumber,
      totalCount,
      pageCount,
      prepareMessage,
      changeEmail,
      selectMessage,
      setPageNumber,
      refresh,
      flushMessage,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages, loading, selectedMessage, pageNumber, totalCount, pageCount, changingSeenFlag, searchQuery],
  );

  return value;
};
